import React, { useMemo, useRef, useState } from 'react';
import { ChevronDown, Search, Check } from 'lucide-react';
import {
    getCountries,
    getCountryCallingCode,
    type Country as RpnCountry,
} from 'react-phone-number-input';
import en from 'react-phone-number-input/locale/en.json';
import flags from 'react-phone-number-input/flags';
import Popover from './Popover';
import Modal from './Modal';
import MarketingModal from '../MarketingModal';
import { useMediaQuery } from '../../hooks/useMediaQuery';

export interface Country {
    code: string;
    name: string;
    dialCode: string;
}

const LABELS = en as Record<string, string>;

export const COUNTRIES: Country[] = getCountries()
    .map((cc): Country => ({
        code: cc,
        name: LABELS[cc] || cc,
        dialCode: `+${getCountryCallingCode(cc)}`,
    }))
    .sort((a, b) => a.name.localeCompare(b.name));

const POPULAR_CODES = ['NG', 'US', 'GB', 'CA', 'GH', 'KE', 'ZA'];
const POPULAR: Country[] = POPULAR_CODES.map((cc) =>
    COUNTRIES.find((c) => c.code === cc),
).filter((c): c is Country => Boolean(c));

const FALLBACK_COUNTRY: Country =
    COUNTRIES.find((c) => c.code === 'GB') || COUNTRIES[0];

const resolveDefault = (code?: string): Country => {
    if (!code) return FALLBACK_COUNTRY;
    const upper = code.toUpperCase();
    return COUNTRIES.find((c) => c.code === upper) || FALLBACK_COUNTRY;
};

const findCountryFromValue = (
    value: string,
    fallback: Country,
): { country: Country; national: string } => {
    const trimmed = (value ?? '').trim();
    if (!trimmed) return { country: fallback, national: '' };

    const sorted = [...COUNTRIES].sort(
        (a, b) => b.dialCode.length - a.dialCode.length,
    );
    for (const c of sorted) {
        if (trimmed.startsWith(c.dialCode)) {
            return {
                country: c,
                national: trimmed.slice(c.dialCode.length).trim(),
            };
        }
    }
    return { country: fallback, national: trimmed };
};

interface FlagIconProps {
    code: string;
    title?: string;
}

const FlagIcon: React.FC<FlagIconProps> = ({ code, title }) => {
    const FlagComp = flags[code as RpnCountry];
    if (!FlagComp) {
        return (
            <span aria-hidden className="phone-flag-fallback">
                {code}
            </span>
        );
    }
    return (
        <span className="phone-flag" aria-hidden>
            <FlagComp title={title || code} />
        </span>
    );
};

interface PhoneInputWithCountryProps {
    id?: string;
    name?: string;
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    required?: boolean;
    disabled?: boolean;
    /** ISO 3166-1 alpha-2 country code shown when `value` is empty. Defaults to GB. */
    defaultCountry?: string;
    marketing?: boolean;
}

const PhoneInputWithCountry: React.FC<PhoneInputWithCountryProps> = ({
    id,
    name,
    value,
    onChange,
    placeholder = 'Phone number',
    required,
    disabled,
    defaultCountry,
    marketing = false,
}) => {
    const PickerModal = marketing ? MarketingModal : Modal;
    const isMobile = useMediaQuery('(max-width: 639px)');
    const [open, setOpen] = useState(false);
    const triggerRef = useRef<HTMLButtonElement>(null);

    const [fallback, setFallback] = useState(() => resolveDefault(defaultCountry));
    const parsed = findCountryFromValue(value, fallback);
    const country = parsed.country;
    const national = parsed.national;

    const emit = (next: Country, nat: string) => {
        const cleaned = nat.replace(/[^\d\s-]/g, '').trim();
        onChange(cleaned ? `${next.dialCode} ${cleaned}` : '');
    };

    const handleNationalChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        emit(country, e.target.value);
    };

    const handleSelectCountry = (c: Country) => {
        setFallback(c);
        setOpen(false);
        emit(c, national);
    };

    return (
        <div className="phone-input-wrap">
            <div className="phone-input-row">
                <button
                    ref={triggerRef}
                    type="button"
                    onClick={() => !disabled && setOpen((o) => !o)}
                    disabled={disabled}
                    aria-haspopup="listbox"
                    aria-expanded={open}
                    aria-label={`Country: ${country.name}, dial code ${country.dialCode}`}
                    className="phone-input-trigger"
                >
                    <FlagIcon code={country.code} title={country.name} />
                    <span className="phone-input-dial">{country.dialCode}</span>
                    <ChevronDown size={14} aria-hidden />
                </button>
                <input
                    id={id}
                    name={name}
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel-national"
                    className="form-input phone-input-national"
                    value={national}
                    onChange={handleNationalChange}
                    placeholder={placeholder}
                    required={required}
                    disabled={disabled}
                />
            </div>

            {isMobile ? (
                <PickerModal
                    isOpen={open}
                    onClose={() => setOpen(false)}
                    title="Select country"
                    maxWidth="500px"
                >
                    <CountryPickerPanel
                        selected={country}
                        onSelect={handleSelectCountry}
                    />
                </PickerModal>
            ) : (
                <Popover
                    open={open}
                    onOpenChange={setOpen}
                    anchor={triggerRef.current}
                    placement="bottom-start"
                    matchAnchorWidth={false}
                    className={`phone-popover ${marketing ? 'marketing-phone-popover' : ''}`}
                >
                    <CountryPickerPanel
                        selected={country}
                        onSelect={handleSelectCountry}
                    />
                </Popover>
            )}
        </div>
    );
};

interface PickerPanelProps {
    selected: Country;
    onSelect: (c: Country) => void;
}

const CountryPickerPanel: React.FC<PickerPanelProps> = ({
    selected,
    onSelect,
}) => {
    const [search, setSearch] = useState('');
    const inputRef = useRef<HTMLInputElement>(null);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return null;
        return COUNTRIES.filter(
            (c) =>
                c.name.toLowerCase().includes(q) ||
                c.dialCode.includes(q) ||
                c.code.toLowerCase().includes(q),
        );
    }, [search]);

    return (
        <div className="phone-picker">
            <div className="phone-picker__search">
                <Search
                    size={14}
                    aria-hidden
                    className="phone-picker__search-icon"
                />
                <input
                    ref={inputRef}
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search country or code"
                    autoFocus
                    aria-label="Search countries"
                    className="phone-picker__search-input"
                />
            </div>
            <div className="phone-picker__list" role="listbox">
                {filtered ? (
                    filtered.length === 0 ? (
                        <div className="phone-picker__empty">No matches</div>
                    ) : (
                        filtered.map((c) => (
                            <CountryRow
                                key={c.code}
                                country={c}
                                selected={selected.code === c.code}
                                onSelect={onSelect}
                            />
                        ))
                    )
                ) : (
                    <>
                        <div className="phone-picker__section">Popular</div>
                        {POPULAR.map((c) => (
                            <CountryRow
                                key={`pop-${c.code}`}
                                country={c}
                                selected={selected.code === c.code}
                                onSelect={onSelect}
                            />
                        ))}
                        <div className="phone-picker__section">
                            All countries
                        </div>
                        {COUNTRIES.map((c) => (
                            <CountryRow
                                key={c.code}
                                country={c}
                                selected={selected.code === c.code}
                                onSelect={onSelect}
                            />
                        ))}
                    </>
                )}
            </div>
        </div>
    );
};

interface CountryRowProps {
    country: Country;
    selected: boolean;
    onSelect: (c: Country) => void;
}

const CountryRow: React.FC<CountryRowProps> = ({
    country,
    selected,
    onSelect,
}) => (
    <button
        type="button"
        role="option"
        aria-selected={selected}
        onClick={() => onSelect(country)}
        className={`phone-picker__row${selected ? ' is-selected' : ''}`}
    >
        <FlagIcon code={country.code} title={country.name} />
        <span className="phone-picker__name">{country.name}</span>
        <span className="phone-picker__dial">{country.dialCode}</span>
        {selected && (
            <Check size={14} aria-hidden className="phone-picker__check" />
        )}
    </button>
);

export default PhoneInputWithCountry;
